/**
 * Salty Pride collection and search pages: filters, sort and Load more without page reloads.
 * Uses Shopify's Section Rendering API to re-render the page's template sections (header count,
 * filter bar, grid) for the new URL, then swaps them in place and updates the address bar.
 * Everything is a normal link or GET form underneath, so it still works without JavaScript.
 */

const templateSectionIds = () =>
  [...document.querySelectorAll('[id^="shopify-section-template--"]')]
    .map((el) => el.id.replace('shopify-section-', ''))
    .slice(0, 5);

let busy = false;

/** Keep requests on the theme being previewed (unpublished themes), not the live one. */
function withPreviewTheme(url) {
  const theme = /** @type {any} */ (window).Shopify?.theme;
  if (theme?.id && theme.role !== 'main') url.searchParams.set('preview_theme_id', String(theme.id));
  return url;
}

async function renderUrl(url, { push = true, keepOpen = null } = {}) {
  if (busy) return;
  busy = true;
  document.documentElement.classList.add('sp-loading');
  try {
    const ids = templateSectionIds();
    const target = new URL(url, window.location.origin);
    withPreviewTheme(target);
    // Shopify needs literal commas between section IDs, so append this by hand (not URLSearchParams).
    const requestUrl = `${target.toString()}${target.search ? '&' : '?'}sections=${ids.join(',')}`;
    const response = await fetch(requestUrl);
    if (!response.ok) throw new Error(String(response.status));
    const sections = await response.json();

    for (const id of ids) {
      const html = sections[id];
      const current = document.getElementById(`shopify-section-${id}`);
      if (!html || !current) continue;
      const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(`shopify-section-${id}`);
      if (fresh) current.replaceWith(fresh);
    }

    if (keepOpen !== null) {
      document.querySelectorAll('[data-sp-facet]')[keepOpen]?.setAttribute('open', '');
    }

    const clean = new URL(url, window.location.origin);
    clean.searchParams.delete('sections');
    clean.searchParams.delete('page');
    clean.searchParams.delete('preview_theme_id');
    if (push) history.pushState({ spCollection: true }, '', clean.toString());
  } catch (error) {
    window.location.href = url;
  } finally {
    busy = false;
    document.documentElement.classList.remove('sp-loading');
  }
}

function formUrl(form) {
  const url = new URL(form.action, window.location.origin);
  const params = new URLSearchParams();
  for (const [key, value] of new FormData(form)) {
    if (value === '') continue;
    params.append(key, String(value));
  }
  // Keep the search query from the form's action (search pages).
  for (const [key, value] of url.searchParams) {
    if (!params.has(key)) params.append(key, value);
  }
  url.search = params.toString();
  return url.toString();
}

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLElement)) return;
  const form = input.closest('[data-sp-facets-form]');
  if (!(form instanceof HTMLFormElement)) return;

  const facet = input.closest('[data-sp-facet]');
  const keepOpen = facet ? [...document.querySelectorAll('[data-sp-facet]')].indexOf(facet) : null;

  // Checkboxes and sort apply right away; price inputs wait for Done.
  if (input.matches('input[type="checkbox"], select')) {
    renderUrl(formUrl(form), { keepOpen });
  }
});

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-sp-facets-form]')) return;
  event.preventDefault();
  renderUrl(formUrl(form));
});

document.addEventListener('click', async (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const link = target.closest('[data-sp-facet-link]');
  if (link instanceof HTMLAnchorElement) {
    event.preventDefault();
    renderUrl(link.href);
    return;
  }

  const more = target.closest('[data-sp-load-more-button]');
  if (more instanceof HTMLAnchorElement) {
    event.preventDefault();
    more.setAttribute('aria-disabled', 'true');
    try {
      const response = await fetch(withPreviewTheme(new URL(more.href, window.location.origin)).toString(), {
        headers: { Accept: 'text/html' },
      });
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const grid = document.querySelector('.product-grid');
      const nextGrid = doc.querySelector('.product-grid');
      if (!grid || !nextGrid) throw new Error('grid');
      nextGrid.querySelectorAll(':scope > .product-grid__item').forEach((item) => grid.append(item));
      const block = document.querySelector('[data-sp-load-more]');
      const nextBlock = doc.querySelector('[data-sp-load-more]');
      if (block && nextBlock) block.replaceWith(nextBlock);
      else block?.remove();
    } catch {
      window.location.href = more.href;
    }
    return;
  }

  // Close open filter menus when clicking elsewhere.
  document.querySelectorAll('[data-sp-facet][open]').forEach((details) => {
    if (!details.contains(target)) details.removeAttribute('open');
  });
});

// Only one filter menu open at a time.
document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.matches('[data-sp-facet]') || !details.open) return;
    document.querySelectorAll('[data-sp-facet][open]').forEach((other) => {
      if (other !== details) other.removeAttribute('open');
    });
  },
  true
);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  const open = document.querySelector('[data-sp-facet][open]');
  if (open) {
    open.removeAttribute('open');
    open.querySelector('summary')?.focus();
  }
});

window.addEventListener('popstate', () => {
  if (document.querySelector('[data-sp-facets]')) renderUrl(window.location.href, { push: false });
});
